import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/* ─── Shared slide-over drawer ───

   Extracted from AuditLog's Change Details drawer so the MCP Analytics Search
   Activity details slide-over (MS-702 AC5) is the same surface rather than a
   look-alike: same backdrop, same left-edge drag handle, same Escape handling,
   same focus move to the close button.

   Only the *shell* is shared. The body is the caller's, because a drawer that
   knows how to render both an audit entry and a search row would have to grow a
   discriminator and would stop being reusable at the third caller. */

// Anchored to the right of the 260px left nav: the drawer defaults to 70% of the
// remaining content pane and can be dragged wider, but its left edge must never
// cross the nav.
const DRAWER_NAV_WIDTH = 260
const DRAWER_MIN_WIDTH = 360

function defaultDrawerWidth() {
  return Math.max(DRAWER_MIN_WIDTH, (window.innerWidth - DRAWER_NAV_WIDTH) * 0.7)
}

export interface DetailDrawerProps {
  isOpen: boolean
  title: string
  onClose: () => void
  /**
   * Changes when a *different* record is shown. The drawer resets its width to the
   * 70% default on change, so a width dragged for one record does not silently
   * become the width for the next.
   */
  recordKey?: string | null
  testId?: string
  children?: ReactNode
}

export function DetailDrawer({
  isOpen,
  title,
  onClose,
  recordKey = null,
  testId,
  children,
}: DetailDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  const [width, setWidth] = useState(defaultDrawerWidth)
  const draggingRef = useRef(false)

  useEffect(() => {
    if (recordKey) setWidth(defaultDrawerWidth())
  }, [recordKey])

  useEffect(() => {
    if (!isOpen) return
    closeRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  // `inert` is React 19's boolean prop; on React 18 it is an unrecognised attribute,
  // so it has to be given the empty string the HTML spec defines and left off
  // entirely when the drawer is open.
  const inertWhenClosed: Record<string, unknown> = isOpen ? {} : { inert: '' }

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.28)',
          opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity .2s', zIndex: 900,
        }}
      />

      {/* Slide-over panel.

          The panel stays mounted so it can slide rather than appear, which means
          that while closed it holds focusable children (Close, the resize handle,
          any links in the body) off the right-hand edge. `aria-hidden` on its own
          is the wrong tool for that: it hides them from a screen reader while
          leaving them in the tab order, which is the one combination that strands a
          keyboard user on a control they cannot perceive.

          `inert` is what closes it — it removes the subtree from both the tab order
          and the accessibility tree. `aria-hidden` stays alongside it, and the pair
          is not the anti-pattern it resembles: the hidden-but-focusable hazard is
          gone, and tooling that does not model `inert` yet (jsdom, and so
          testing-library and axe) still reads the closed panel as hidden. */}
      <aside
        {...inertWhenClosed}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!isOpen}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width, maxWidth: '92vw',
          background: 'var(--bg-card)', borderLeft: '1px solid var(--border)',
          boxShadow: '-6px 0 18px rgba(0,0,0,.08)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform .22s ease-out',
          display: 'flex', flexDirection: 'column', zIndex: 901,
        }}
      >
        {/* Drag handle — resizes the drawer by its left edge */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('drawer.resize')}
          onMouseDown={e => {
            e.preventDefault()
            draggingRef.current = true
            document.body.style.cursor = 'ew-resize'
            document.body.style.userSelect = 'none'

            function onMove(ev: MouseEvent) {
              if (!draggingRef.current) return
              const maxWidth = window.innerWidth - DRAWER_NAV_WIDTH
              setWidth(Math.max(DRAWER_MIN_WIDTH, Math.min(window.innerWidth - ev.clientX, maxWidth)))
            }
            function onUp() {
              draggingRef.current = false
              document.body.style.cursor = ''
              document.body.style.userSelect = ''
              document.removeEventListener('mousemove', onMove)
              document.removeEventListener('mouseup', onUp)
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
          }}
          style={{
            position: 'absolute', top: 0, left: 0, bottom: 0, width: 7,
            cursor: 'ew-resize', zIndex: 2,
          }}
        />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid var(--border-light)', flexShrink: 0,
        }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('drawer.close')}
            className="icon-btn"
            style={{ fontSize: 22, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {children}
      </aside>
    </>
  )
}

/** A labelled read-only field inside a drawer body. `mono` for identifiers; `block`
 *  for a preformatted payload (a JSON array), which keeps its own whitespace. */
export function DrawerField({
  label,
  value,
  mono = false,
  block = false,
}: {
  label: string
  value: string
  mono?: boolean
  block?: boolean
}) {
  return (
    <div className="audit-drawer-field" style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 4,
      }}>
        {label}
      </div>
      {block ? (
        <pre style={{
          background: 'var(--bg-app)', border: '1px solid var(--border-light)', borderRadius: 8,
          padding: 12, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre', margin: 0,
        }}>
          {value}
        </pre>
      ) : (
        <div style={{
          fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.45, wordBreak: 'break-word',
          ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 } : {}),
        }}>
          {value}
        </div>
      )}
    </div>
  )
}

/** A drawer field whose value is arbitrary markup rather than a string — a badge,
 *  or a name and email side by side. */
export function DrawerNodeField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="audit-drawer-field" style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}
